import { makeAutoObservable } from 'mobx';
import {
  CustomBenefitConstraint,
  BenefitEnum,
  CompanyCustomBenefitDetail,
  CompanyCustomBenefitPatchRequest,
  CompanyCustomBenefitRequest,
  CustomBenefitConstraintRequest,
  ConstraintTypeEnum,
  IconEnum,
  HeaderImageFromGalleryEnum,
  CustomBenefitStatusEnum
} from '@pxtra-client-lib-legacy/gen';
import { FileWithPreview } from '../../../../lib/components';
import { getBase64 } from '../../../../lib/util';
import Util from '../../../../misc/util';

const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));

type AmountChangeCallback = (employeeId: string) => void;
type FocusChangeCallback = (employeeId: string) => void;

class CreateCustomBenefitStore {
  private _name_de = '';
  private _name_en = '';
  private _buttonLink_de = '';
  private _buttonLink_en = '';
  private _buttonLabel_de = '';
  private _buttonLabel_en = '';
  private _icon = 'settings';
  private _description_de = '';
  private _description_en = '';
  private _headerImagePreview: FileWithPreview | undefined;
  private _headerImageFromGallery: HeaderImageFromGalleryEnum | undefined;
  private _influenceBenefit = false;
  private _influencedBenefit: BenefitEnum | undefined;
  private _constraintType: ConstraintTypeEnum = 'replace';
  private _companyAmount: number | undefined = undefined;
  private _companyStart: Date = nextMonth;

  private _employeeAssignmentsStart: Date | undefined = undefined;
  private _companyAmountSet = false;
  private _applyPendingAssignments = false;
  private _correctPendingAssignments = false;

  private _status: CustomBenefitStatusEnum | undefined = undefined;
  private _backButtonClicked = false;
  private _translationAdded = false;
  private _editingActiveBenefit = false;
  private _partialEditingOnly = false;
  private _allowEditTranslationTitle = false;
  private _editingBenefitId: string | undefined;
  private _companyAmountUpdated = false;
  private _allowBothMobilityBenefits = false;

  private _changedFields: string[] = [];
  private _initialHeaderImageName = '';

  private focusSubscribers: Map<string, AmountChangeCallback[]> = new Map();

  get name_de(): string {
    return this._name_de;
  }
  set name_de(value: string) {
    this._name_de = value;
    this._changedFields.push('name_de');
  }

  get name_en(): string {
    return this._name_en;
  }
  set name_en(value: string) {
    this._name_en = value;
    this._changedFields.push('name_en');
  }

  get buttonLink_de(): string {
    return this._buttonLink_de;
  }
  set buttonLink_de(value: string) {
    this._buttonLink_de = value;
    this._changedFields.push('buttonLink_de');
  }

  get buttonLink_en(): string {
    return this._buttonLink_en;
  }
  set buttonLink_en(value: string) {
    this._buttonLink_en = value;
    this._changedFields.push('buttonLink_en');
  }

  get buttonLabel_de(): string {
    return this._buttonLabel_de;
  }
  set buttonLabel_de(value: string) {
    this._buttonLabel_de = value;
    this._changedFields.push('buttonLabel_de');
  }

  get buttonLabel_en(): string {
    return this._buttonLabel_en;
  }
  set buttonLabel_en(value: string) {
    this._buttonLabel_en = value;
    this._changedFields.push('buttonLabel_en');
  }

  get icon(): string {
    return this._icon;
  }
  set icon(value: string) {
    this._icon = value;
    this._changedFields.push('icon');
  }

  get description_de(): string {
    return this._description_de;
  }
  set description_de(value: string) {
    this._description_de = value;
    this._changedFields.push('description_de');
  }

  get description_en(): string {
    return this._description_en;
  }
  set description_en(value: string) {
    this._description_en = value;
    this._changedFields.push('description_en');
  }

  get headerImagePreview(): FileWithPreview | undefined {
    return this._headerImagePreview;
  }
  set headerImagePreview(value: FileWithPreview | undefined) {
    if (this._headerImagePreview !== value) {
      this._changedFields.push('headerImage');
    }
    this._headerImagePreview = value;
  }

  get headerImageFromGallery(): HeaderImageFromGalleryEnum | undefined {
    return this._headerImageFromGallery;
  }
  set headerImageFromGallery(value: HeaderImageFromGalleryEnum | undefined) {
    if (this._headerImageFromGallery !== value) {
      this._changedFields.push('headerImageFromGallery');
    }
    this._headerImageFromGallery = value;
  }

  get influenceBenefit(): boolean {
    return this._influenceBenefit;
  }
  set influenceBenefit(value: boolean) {
    this._influenceBenefit = value;
    this._changedFields.push('influencedBenefit');
  }

  get influencedBenefit(): BenefitEnum | undefined {
    return this._influencedBenefit;
  }
  set influencedBenefit(value: BenefitEnum | undefined) {
    this._influencedBenefit = value;
    this._changedFields.push('influencedBenefit');
  }

  get constraintType(): ConstraintTypeEnum {
    return this._constraintType;
  }
  set constraintType(value: ConstraintTypeEnum) {
    this._constraintType = value;
    this._changedFields.push('influencedBenefit');
  }

  get backButtonClicked(): boolean {
    return this._backButtonClicked;
  }
  set backButtonClicked(value: boolean) {
    this._backButtonClicked = value;
  }

  get translationAdded(): boolean {
    return this._translationAdded;
  }
  set translationAdded(value: boolean) {
    this._translationAdded = value;
  }

  get editingBenefitId(): string | undefined {
    return this._editingBenefitId;
  }
  set editingBenefitId(value: string | undefined) {
    this._editingBenefitId = value;
  }

  get editingActiveBenefit(): boolean {
    return this._editingActiveBenefit;
  }

  get partialEditingOnly(): boolean {
    return this._partialEditingOnly;
  }

  get allowEditTranslationTitle(): boolean {
    return this._allowEditTranslationTitle;
  }

  get companyStart(): Date {
    return this._companyStart;
  }
  set companyStart(value: Date) {
    this._companyStart = value;
    this._changedFields.push('companyStart');
  }
  get employeeAssignmentsStart(): Date | undefined {
    return this._employeeAssignmentsStart;
  }
  set employeeAssignmentsStart(value: Date | undefined) {
    this._employeeAssignmentsStart = value;
  }

  get companyAmountSet(): boolean {
    return this._companyAmountSet;
  }
  set companyAmountSet(value: boolean) {
    this._companyAmountSet = value;
  }
  set applyPendingAssignments(value: boolean) {
    this._applyPendingAssignments = value;
  }
  set correctPendingAssignments(value: boolean) {
    this._correctPendingAssignments = value;
  }
  get companyAmount(): number | undefined {
    return this._companyAmount;
  }
  set companyAmount(value: number | undefined) {
    this._companyAmount = value;
    this.companyAmountUpdated = true;
    this._changedFields.push('company_amount');
  }

  get companyAmountUpdated(): boolean {
    return this._companyAmountUpdated;
  }
  set companyAmountUpdated(value: boolean) {
    this._companyAmountUpdated = value;
  }

  set status(value: CustomBenefitStatusEnum | undefined) {
    this._status = value;
  }
  get status(): CustomBenefitStatusEnum | undefined {
    return this._status;
  }

  set allowBothMobilityBenefits(value: boolean) {
    this._allowBothMobilityBenefits = value;
  }
  get allowBothMobilityBenefits(): boolean {
    return this._allowBothMobilityBenefits;
  }

  get changedFields(): string[] {
    return this._changedFields;
  }

  set focusedEmployeeAmount(value: string | undefined) {
    this.notifyFocusChange(value);
  }

  public clear() {
    this.name_de = '';
    this.name_en = '';
    this.buttonLink_de = '';
    this.buttonLink_en = '';
    this.buttonLabel_de = '';
    this.buttonLabel_en = '';
    this.icon = 'settings';
    this.description_de = '';
    this.description_en = '';
    this.headerImagePreview = undefined;
    this.headerImageFromGallery = undefined;
    this.influenceBenefit = false;
    this.influencedBenefit = undefined;
    this.constraintType = 'replace';
    this.backButtonClicked = false;
    this.translationAdded = false;
    this.editingBenefitId = undefined;
    this._editingActiveBenefit = false;
    this._partialEditingOnly = false;
    this.companyAmount = undefined;
    this.companyAmountUpdated = false;
    this.companyStart = nextMonth;
    this.employeeAssignmentsStart = undefined;
    this.companyAmountSet = false;
    this.applyPendingAssignments = false;
    this.correctPendingAssignments = false;
    this.allowBothMobilityBenefits = false;
    this._status = undefined;
    this._changedFields = [];
  }
  public clear_en() {
    this.name_en = '';
    this.buttonLink_en = '';
    this.buttonLabel_en = '';
    this.description_en = '';
    this.translationAdded = false;
  }

  public get canBuildRequest() {
    return (
      (!!this._name_de &&
        !!this._icon &&
        (!!this._headerImagePreview || !!this._headerImageFromGallery)) ||
      (this._status === 'draft' && !!this._name_de && !!this._icon)
    );
  }

  constructor() {
    makeAutoObservable(this, { buildCustomBenefitRequest: false });
  }

  private notifyFocusChange(employeeId?: string) {
    if (employeeId === undefined) {
      this.focusSubscribers.forEach((subscribers, employeeId) => {
        subscribers.forEach((callback) => callback(employeeId));
      });
    } else {
      const subscribers = this.focusSubscribers.get(employeeId) || [];
      subscribers.forEach((callback) => callback(employeeId));
    }
  }

  public subscribeToFocusChange(employeeId: string, callback: FocusChangeCallback) {
    if (!this.focusSubscribers.has(employeeId)) {
      this.focusSubscribers.set(employeeId, []);
    }
    this.focusSubscribers.get(employeeId)?.push(callback);
  }

  public unsubscribeFromFocusChange(employeeId: string, callback: FocusChangeCallback) {
    const subscribers = this.focusSubscribers.get(employeeId) || [];
    this.focusSubscribers.set(
      employeeId,
      subscribers.filter((sub) => sub !== callback)
    );
  }

  private buildRequest = async () => ({
    name_de: this._name_de,
    name_en: this._name_en,
    button_link_de: this._buttonLink_de,
    button_link_en: this._buttonLink_en,
    button_label_de: this._buttonLabel_de,
    button_label_en: this._buttonLabel_en,
    icon: this._icon,
    description_de: this._description_de,
    description_en: this._description_en,
    header_image:
      this._headerImagePreview?.preview !== undefined
        ? {
            name: this._headerImagePreview?.name,
            encoded_string: await getBase64(this._headerImagePreview)
          }
        : undefined,
    header_image_from_gallery: this._headerImageFromGallery,
    constraints:
      this._influenceBenefit && this._influencedBenefit
        ? ([
            {
              benefit: this._influencedBenefit as BenefitEnum,
              constraint_type: this._constraintType as ConstraintTypeEnum
            } as CustomBenefitConstraint
          ] as CustomBenefitConstraintRequest[])
        : [],
    status: this._status ?? 'draft',
    allow_both_mobility_benefits: this._allowBothMobilityBenefits
  });

  private buildPatchRequest = async () => {
    const patchRequest: CompanyCustomBenefitPatchRequest = {
      icon: this._icon as IconEnum,
      apply_pending_assignments: this._applyPendingAssignments,
      correct_pending_assignments: this._correctPendingAssignments,
      start_accounting_period: {
        year: this._employeeAssignmentsStart?.getFullYear() || nextMonth.getFullYear(),
        month: (this._employeeAssignmentsStart?.getMonth() || nextMonth.getMonth()) + 1
      }
    };

    if (this._changedFields.includes('name_de')) {
      patchRequest.name_de = this._name_de;
    }
    if (this._changedFields.includes('name_en')) {
      patchRequest.name_en = this._name_en;
    }

    if (this._changedFields.includes('buttonLink_de')) {
      patchRequest.button_link_de = this._buttonLink_de;
    }
    if (this._changedFields.includes('buttonLink_en')) {
      patchRequest.button_link_en = this._buttonLink_en;
    }

    if (this._changedFields.includes('buttonLabel_de')) {
      patchRequest.button_label_de = this._buttonLabel_de;
    }
    if (this._changedFields.includes('buttonLabel_en')) {
      patchRequest.button_label_en = this._buttonLabel_en;
    }

    if (this._changedFields.includes('description_de')) {
      patchRequest.description_de = this._description_de;
    }
    if (this._changedFields.includes('description_en')) {
      patchRequest.description_en = this._description_en;
    }

    if (
      this._headerImagePreview?.name !== undefined &&
      this._initialHeaderImageName !== this._headerImagePreview?.name &&
      this._headerImagePreview?.preview !== undefined
    ) {
      patchRequest.header_image = {
        name: this._headerImagePreview.name,
        encoded_string: String(await getBase64(this._headerImagePreview))
      };
    }
    if (this._changedFields.includes('headerImageFromGallery')) {
      patchRequest.header_image_from_gallery = this._headerImageFromGallery
        ? this._headerImageFromGallery
        : '';
    }

    if (this._changedFields.includes('companyStart')) {
      patchRequest.start_accounting_period = Util.getAccountingPeriod(this._companyStart);
    }

    if (this._changedFields.includes('influencedBenefit')) {
      if (!this.influenceBenefit) {
        patchRequest.constraints = [];
      } else if (this._influencedBenefit !== undefined) {
        patchRequest.constraints = [
          {
            benefit: this._influencedBenefit as BenefitEnum,
            constraint_type: this._constraintType as ConstraintTypeEnum
          }
        ];
      }
    }

    if (this.companyAmountUpdated) patchRequest.company_amount = this._companyAmount;

    if (this.status) patchRequest.status = this._status;

    patchRequest.allow_both_mobility_benefits = this._allowBothMobilityBenefits;

    return patchRequest;
  };

  public buildCustomBenefitRequest: () => Promise<CompanyCustomBenefitRequest | undefined> =
    async () => {
      if (!this._editingBenefitId && this.canBuildRequest) {
        return (await this.buildRequest()) as CompanyCustomBenefitRequest;
      }
      return undefined;
    };

  public buildCustomBenefitPatchRequest: () => Promise<
    CompanyCustomBenefitPatchRequest | undefined
  > = async () => {
    if (this._editingBenefitId)
      return (await this.buildPatchRequest()) as CompanyCustomBenefitPatchRequest;
    return undefined;
  };

  public loadCustomBenefit: (customBenefit: CompanyCustomBenefitDetail | undefined | null) => void =
    (customBenefit) => {
      if (customBenefit) {
        this._name_de = customBenefit.name_de ?? '';
        this._buttonLink_de = customBenefit.button_link_de ?? '';
        this._buttonLabel_de = customBenefit.button_label_de ?? '';
        this._icon = customBenefit.icon ?? 'settings';
        this._description_de = customBenefit.description_de ?? '';
        if (customBenefit.header_image) {
          this._headerImagePreview = {
            name: customBenefit.header_image.name,
            webkitRelativePath: customBenefit.header_image.url,
            preview: customBenefit.header_image.url,
            type: 'image/'
          } as FileWithPreview;
          this._initialHeaderImageName = customBenefit.header_image.name;
        }
        this._headerImageFromGallery =
          customBenefit.header_image_from_gallery as HeaderImageFromGalleryEnum;
        if (customBenefit.constraints && customBenefit.constraints.length > 0) {
          this._influenceBenefit = true;
          this._influencedBenefit = customBenefit.constraints[0].benefit as BenefitEnum;
          this._constraintType = customBenefit.constraints[0].constraint_type as ConstraintTypeEnum;
        }
        if (customBenefit.start_accounting_period) {
          this._companyStart = Util.getDate(customBenefit.start_accounting_period, 'start');
        }
        if (
          customBenefit.name_en ||
          customBenefit.button_link_en ||
          customBenefit.button_label_en ||
          customBenefit.description_en
        ) {
          this._translationAdded = true;
          this._name_en = customBenefit.name_en ?? '';
          this._buttonLink_en = customBenefit.button_link_en ?? '';
          this._buttonLabel_en = customBenefit.button_label_en ?? '';
          this._description_en = customBenefit.description_en ?? '';
        }
        this._editingActiveBenefit = customBenefit.status === 'active';
        this._partialEditingOnly = this._editingActiveBenefit;
        this._allowEditTranslationTitle = this._partialEditingOnly && !!this.name_en;
        if (customBenefit.company_amount !== undefined && customBenefit.company_amount !== null) {
          this._companyAmount = customBenefit.company_amount;
        }
        if (customBenefit.pending_assignments_start) {
          this._employeeAssignmentsStart = Util.getDate(
            customBenefit.pending_assignments_start,
            'start'
          );
        }
        this._companyAmountSet = customBenefit.company_amount_set ?? false;
        this._status = customBenefit.status;
        this._allowBothMobilityBenefits = customBenefit.allow_both_mobility_benefits ?? false;
      }
    };
}

export default CreateCustomBenefitStore;
